import { useState, useEffect, useRef } from "react";
import { getViewAs, getViewAsName, stopViewAs } from "../../lib/viewAs";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Target,
  LayoutDashboard,
  Building2,
  Users,
  Search,
  FileText,
  FileCheck,
  CalendarCheck,
  Receipt,
  MessageSquare,
  Settings,
  Moon,
  Sun,
  MonitorSmartphone,
  LogOut,
  BarChart3,
  Briefcase,
  DollarSign,
  FileBarChart,
  GitBranch,
  Workflow,
  Handshake,
  HeartPulse,
  ShieldCheck,
  Database,
  Scale,
  Globe,
  Stethoscope,
  Wrench,
  ArrowRightLeft,
  LifeBuoy,
  Eye,
  Menu,
  X,
  ListTodo,
  UserCheck,
  ChevronRight,
  ClipboardCheck,
  Landmark,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuth } from "../../lib/AuthContext";
import { useRefreshTrigger } from "../../lib/DataRefreshContext";
import { useSSEInvalidation } from "../../lib/useSSEInvalidation";
import { startMobileTableLabels } from "../../lib/mobileTableLabels";
import { getStoredMode, initTheme, nextMode, resolveMode, setThemeMode } from "../../lib/theme";
import { getSidebarCounts } from "../../lib/api";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import MyMailboxButton from "./MyMailboxButton";
import NotificationsButton from "./NotificationsButton";
import AssistantPanel from "../assistant/AssistantPanel";

// adminOnly items are hidden for a key account manager. The backend refuses
// them as well (middleware/scope.py), so hiding here is convenience, not the
// security boundary.
// hidden items stay routable by URL but are not shown in the sidebar (Fan, 2026-10-12).
export const navigation = [
  { name: "My Clients", href: "/my-clients", icon: Briefcase, accountManagerOnly: true },
  { name: "My Targets", href: "/my-targets", icon: Target, accountManagerOnly: true },
  { name: "My Reviews", href: "/my-reviews", icon: ClipboardCheck, accountManagerOnly: true },
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard, adminOnly: true },
  { name: "Inbox", href: "/inbox", icon: MessageSquare, countKey: "inbox_unread", alertCountKey: "pending_chasers" },
  { name: "Tasks", href: "/tasks", icon: ListTodo, countKey: "my_open_tasks" },
  { name: "Enquiries", href: "/searches", icon: Search, countKey: "active_searches" },
  { name: "Options", href: "/options", icon: FileText, countKey: "pending_options" },
  { name: "Quotes", href: "/quotes", icon: FileCheck, countKey: "pending_quotes" },
  { name: "Viewings", href: "/viewings", icon: Eye },
  { name: "Bookings", href: "/bookings", icon: CalendarCheck },
  { name: "Invoices", href: "/invoices", icon: Receipt },
  { name: "Guest Issues", href: "/guest-issues", icon: LifeBuoy },
  { section: "Databases" },
  { name: "Properties", href: "/properties", icon: Building2 },
  { name: "Suppliers", href: "/suppliers", icon: Users },
  { name: "Clients", href: "/clients", icon: Briefcase },
  { name: "Guests", href: "/guests", icon: UserCheck },
  { section: "Pipelines", adminOnly: true },
  { name: "Suppliers", href: "/supplier-pipeline", icon: GitBranch, countKey: "supplier_pipeline_actions", adminOnly: true },
  { name: "Clients", href: "/client-pipeline", icon: Workflow, countKey: "client_pipeline_actions", adminOnly: true },
  { name: "Partners", href: "/partner-pipeline", icon: Handshake, adminOnly: true },
  { name: "Tenders", href: "/tenders", icon: Landmark, adminOnly: true },
  { section: "Reporting & Analytics", adminOnly: true },
  { name: "Analytics", href: "/analytics", icon: BarChart3, adminOnly: true },
  { name: "Financials", href: "/financials", icon: DollarSign, adminOnly: true },
  { name: "Reports", href: "/reports", icon: FileBarChart, adminOnly: true },
  { name: "Integrations", href: "/integrations", icon: Settings, adminOnly: true },
  { section: "Admin", adminOnly: true },
  { name: "Team & Access", href: "/admin/team-access", icon: ShieldCheck, adminOnly: true },
  // HR records: Fan only (the backend refuses everyone else too).
  { name: "Staff Reviews", href: "/admin/staff-reviews", icon: ClipboardCheck, adminOnly: true, ownerOnly: true },
  { name: "Approvals", hidden: true, href: "/admin/approvals", icon: ShieldCheck, alertCountKey: "approvals_combined", adminOnly: true },
  { name: "Classifieds", hidden: true, href: "/admin/classifieds", icon: Globe, adminOnly: true },
  { name: "Budget Basis", hidden: true, href: "/admin/budget-basis", icon: Scale, adminOnly: true },
  { name: "Exchange Rates", href: "/admin/fx-rates", icon: DollarSign, adminOnly: true },
  { name: "Operations", hidden: true, href: "/operations", icon: HeartPulse, adminOnly: true },
  { name: "Data", hidden: true, href: "/data", icon: Database, adminOnly: true },
  { name: "Repair Ops", hidden: true, href: "/admin/repair-ops", icon: Wrench, adminOnly: true },
  { name: "Diagnostics", hidden: true, href: "/admin/diagnostic-browser", icon: Stethoscope, adminOnly: true },
  { name: "Status Migration", hidden: true, href: "/admin/migrations/option-status", icon: ArrowRightLeft, adminOnly: true },
];

// Fan's logins and aliases. ownerOnly items (staff reviews) show only for her.
export const OWNER_EMAILS = ["fan@bonjour-investments.com", "fan.zhang@bonjour-residences.co.uk", "hello@bonjour-residences.co.uk"];

export function visibleNavigation(items, isAdmin, email = "") {
  const isOwner = OWNER_EMAILS.includes((email || "").toLowerCase());
  return items.filter((item) => {
    if (item.hidden) return false;
    if (item.ownerOnly && !isOwner) return false;
    if (item.adminOnly && !isAdmin) return false;
    if (item.accountManagerOnly && isAdmin) return false;
    return true;
  });
}

export default function SidebarLayout() {
  useSSEInvalidation();
  const refreshKey = useRefreshTrigger("dashboard");
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { user, logout } = useAuth();
  const viewAsEmail = getViewAs();
  const viewAsName = getViewAsName();
  const isAdmin = user?.is_admin !== false;
  const navItems = visibleNavigation(navigation, isAdmin, user?.email);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("br_nav_groups") || "{}"); } catch { return {}; }
  });
  const toggleGroup = (name) => setOpenGroups((prev) => {
    const next = { ...prev, [name]: !prev[name] };
    try { localStorage.setItem("br_nav_groups", JSON.stringify(next)); } catch { /* storage full or blocked */ }
    return next;
  });
  const [counts, setCounts] = useState({});
  const countsRef = useRef(counts);
  countsRef.current = counts;

  useEffect(() => {
    const fetchCounts = () => {
      getSidebarCounts()
        .then((res) => setCounts(res.data || {}))
        .catch(() => {});
    };
    fetchCounts();
  }, [refreshKey]);

  useEffect(() => {
    const interval = setInterval(() => {
      getSidebarCounts()
        .then((res) => setCounts(res.data || {}))
        .catch(() => {});
    }, 300000);
    return () => clearInterval(interval);
  }, []);

  // Close the mobile drawer whenever the route changes, otherwise tapping a
  // nav item on a phone leaves the overlay covering the page you just opened.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // On phones, turn every wide table into stacked cards (labels taken from the
  // table's own header row). Idle above 767px.
  useEffect(() => startMobileTableLabels(), []);

  // Light / dark theme. Stored in localStorage, follows the OS on "system".
  const [themeMode, setThemeModeState] = useState(() => getStoredMode());
  useEffect(() => initTheme(), []);
  const cycleTheme = () => setThemeModeState(setThemeMode(nextMode(themeMode)));
  const ThemeIcon = themeMode === "system" ? MonitorSmartphone : (resolveMode(themeMode) === "dark" ? Moon : Sun);
  const themeLabel = themeMode === "system" ? "Theme: system" : (themeMode === "dark" ? "Theme: dark" : "Theme: light");

  // A drawer open over the page must not let the page behind it scroll.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [mobileNavOpen]);

  const initials = user?.name
    ? user.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)
    : "?";
  const displayName = user?.name || user?.email || "User";
  const displayEmail = user?.email || "";

  // Single number on the mobile top bar so you can see there is something to do
  // without opening the drawer. Mirrors the red/orange/blue badges in the nav.
  const mobileBadgeTotal = navItems.reduce((total, item) => {
    if (item.section) return total;
    return total
      + (item.alertCountKey ? (counts[item.alertCountKey] || 0) : 0)
      + (item.secondaryCountKey ? (counts[item.secondaryCountKey] || 0) : 0)
      + (item.countKey ? (counts[item.countKey] || 0) : 0);
  }, 0);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen overflow-hidden" style={viewAsEmail ? { paddingTop: 36 } : undefined}>
      {viewAsEmail && (
        <div
          className="fixed top-0 inset-x-0 z-50 h-9 bg-amber-400 text-amber-950 text-xs font-medium flex items-center justify-center gap-3 px-3"
          data-testid="view-as-banner"
        >
          <span>Viewing BR OS as {viewAsName || viewAsEmail}. Read-only preview.</span>
          <button
            type="button"
            onClick={() => stopViewAs()}
            className="underline underline-offset-2 hover:no-underline"
            data-testid="exit-view-as"
          >
            Exit
          </button>
        </div>
      )}
      {/* Mobile top bar: only rendered below md, where the sidebar is off-canvas */}
      <header
        className="br-mobile-topbar md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-card border-b border-border flex items-center gap-3 px-3"
        data-testid="mobile-topbar"
      >
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open navigation"
          aria-expanded={mobileNavOpen}
          className="relative w-10 h-10 flex items-center justify-center rounded-lg text-foreground hover:bg-muted"
          data-testid="mobile-nav-toggle"
        >
          <Menu className="w-6 h-6" />
          {mobileBadgeTotal > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-semibold px-1"
              data-testid="mobile-nav-badge"
            >
              {mobileBadgeTotal > 99 ? "99+" : mobileBadgeTotal}
            </span>
          )}
        </button>
        <img src="/bonjour-logo-blue.png" alt="Bonjour Residences" className="h-6 w-auto object-contain dark:hidden" />
        <img src="/bonjour-logo.png" alt="" aria-hidden="true" className="h-6 w-auto object-contain hidden dark:block" />
      </header>

      {/* Drawer backdrop */}
      {mobileNavOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-nav/40"
          onClick={() => setMobileNavOpen(false)}
          data-testid="mobile-nav-backdrop"
        />
      )}

      {/* Sidebar: static column from md up, off-canvas drawer below md */}
      <aside
        className={cn(
          "bg-card border-r border-border flex flex-col",
          "br-mobile-drawer fixed inset-y-0 left-0 z-50 w-[260px] transition-transform duration-200 ease-out",
          "md:static md:z-auto md:w-[180px] md:translate-x-0 md:transition-none",
          mobileNavOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="sidebar"
        data-mobile-open={mobileNavOpen ? "true" : "false"}
      >
        {/* Close control, drawer only */}
        <button
          type="button"
          onClick={() => setMobileNavOpen(false)}
          aria-label="Close navigation"
          className="md:hidden absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
          data-testid="mobile-nav-close"
        >
          <X className="w-5 h-5" />
        </button>
        {/* Logo */}
        <div className="flex flex-col items-center py-6 px-4 border-b border-border">
          <div className="w-32 mb-2">
            <img
              src="/bonjour-logo-blue.png"
              alt="Bonjour Residences"
              className="w-full h-auto object-contain dark:hidden"
            />
            <img
              src="/bonjour-logo.png"
              alt=""
              aria-hidden="true"
              className="w-full h-auto object-contain hidden dark:block"
            />
          </div>
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
            Operating System
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 space-y-1 px-2 overflow-y-auto">
          {navItems.map((item, idx) => {
            if (item.section) {
              return (
                <div
                  key={item.section}
                  className="pt-4 pb-1 px-3 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider"
                >
                  {item.section}
                </div>
              );
            }

            if (item.children) {
              const inGroup = location.pathname.startsWith(item.groupPath);
              const expanded = inGroup || !!openGroups[item.name];
              const slug = item.name.toLowerCase().replace(/\s+/g, '-');
              return (
                <div key={`group-${item.name}`}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(item.name)}
                    aria-expanded={expanded}
                    data-testid={`nav-${slug}`}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-3 md:py-2.5 text-sm font-medium rounded-lg transition-all",
                      inGroup ? "text-foreground" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                    )}
                  >
                    <item.icon className={cn("w-5 h-5 flex-shrink-0", inGroup && "text-bonjour-green")} />
                    <span className="flex-1 text-left">{item.name}</span>
                    <ChevronRight className={cn("w-4 h-4 transition-transform", expanded && "rotate-90")} />
                  </button>
                  {expanded && (
                    <div className="mt-1 space-y-1 pl-4" data-testid={`nav-${slug}-children`}>
                      {item.children.map((child) => {
                        const childActive = location.pathname.startsWith(child.href);
                        return (
                          <NavLink
                            key={child.href}
                            to={child.href}
                            data-testid={`nav-${slug}-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                            className={cn(
                              "flex items-center gap-3 px-3 py-2.5 md:py-2 text-sm font-medium rounded-lg transition-all",
                              childActive
                                ? "bg-muted text-foreground"
                                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                            )}
                          >
                            <child.icon className={cn("w-4 h-4 flex-shrink-0", childActive && "text-bonjour-green")} />
                            <span className="flex-1">{child.name}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const isActive = location.pathname === item.href ||
              (item.href !== "/dashboard" && location.pathname.startsWith(item.href));

            return (
              <NavLink
                key={item.href}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  "flex items-center gap-3 px-3 py-3 md:py-2.5 text-sm font-medium rounded-lg transition-all",
                  isActive
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                )}
              >
                <item.icon className={cn("w-5 h-5 flex-shrink-0", isActive && "text-bonjour-green")} />
                <span className="flex-1">{item.name}</span>
                {(item.countKey && counts[item.countKey] > 0) || (item.secondaryCountKey && counts[item.secondaryCountKey] > 0) || (item.alertCountKey && counts[item.alertCountKey] > 0) ? (
                  <span className="ml-auto flex items-center gap-1">
                    {item.alertCountKey && counts[item.alertCountKey] > 0 && (
                      <span className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-semibold px-1.5">
                        {counts[item.alertCountKey]}
                      </span>
                    )}
                    {item.secondaryCountKey && counts[item.secondaryCountKey] > 0 && (
                      <span className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-semibold px-1.5">
                        {counts[item.secondaryCountKey]}
                      </span>
                    )}
                    {item.countKey && counts[item.countKey] > 0 && (
                      <span className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold px-1.5">
                        {counts[item.countKey]}
                      </span>
                    )}
                  </span>
                ) : null}
              </NavLink>
            );
          })}
        </nav>

        {/* User profile */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-sm">
                {initials}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
              <p className="text-xs text-muted-foreground truncate">{displayEmail}</p>
            </div>
          </div>
          {!getViewAs() && <MyMailboxButton />}
          {!getViewAs() && <NotificationsButton />}
          <button
            onClick={cycleTheme}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full mb-2"
            aria-label={themeLabel}
            title={themeLabel}
            data-testid="theme-toggle"
          >
            <ThemeIcon className="w-4 h-4" />
            <span>{themeMode === "system" ? "Theme: auto" : (themeMode === "dark" ? "Dark theme" : "Light theme")}</span>
          </button>
          <NavLink
            to="/settings/feature-flags"
            className={cn(
              "flex items-center gap-2 text-sm transition-colors w-full mb-2",
              location.pathname === "/settings/feature-flags"
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Settings className="w-4 h-4" />
            <span>Feature Flags</span>
          </NavLink>
          <button
            onClick={() => setShowLogoutDialog(true)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full"
            data-testid="sign-out-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="br-mobile-main flex-1 overflow-auto bg-background pt-14 md:pt-0">
        <Outlet />
      </main>

      {/* Assistant: chat that reads everything and acts on Confirm */}
      <AssistantPanel />

      {/* Logout Confirmation */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign Out</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to sign out?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button onClick={handleLogout}>Sign Out</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
